import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ruler, Send, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { SERVICES } from "./Services";
import { useLang } from "@/lib/i18n";

const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const COMMERCIAL = {
  carpet: [15.75, 27],
  lvp: [22.5, 45],
  glue_down: [15.75, 27],
  vinyl_sheet: [22.5, 45],
  laminate: [18, 36],
  restretch: [6.75, 13.5],
};

export default function QuoteCalculator({ onBook }) {
  const { t, lang } = useLang();
  const [mode, setMode] = useState("room");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [yards, setYards] = useState("");
  const [serviceId, setServiceId] = useState("carpet");
  const [result, setResult] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [removeOld, setRemoveOld] = useState(false);
  const [manyCuts, setManyCuts] = useState(false);
  const [seamRepair, setSeamRepair] = useState(false);
  const [subfloor, setSubfloor] = useState(false);
  const [stairs, setStairs] = useState("");
  const [propertyType, setPropertyType] = useState("residential");

  const service = useMemo(
    () => SERVICES.find((s) => s.id === serviceId),
    [serviceId]
  );

  const isCom = propertyType === "commercial";
  const showRemoveOld = serviceId === "carpet";
  const showManyCuts = ["lvp", "glue_down", "vinyl_sheet"].includes(serviceId);
  const showSeam = serviceId === "restretch" && !isCom;
  const showSubfloor = isCom;
  const showStairs = serviceId === "carpet";

  const computedYards = () => {
    if (mode === "room") {
      const l = parseFloat(length);
      const w = parseFloat(width);
      if (!l || !w || l <= 0 || w <= 0) return null;
      return Math.round(((l * w) / 9) * 10) / 10;
    }
    const y = parseFloat(yards);
    if (!y || y <= 0) return null;
    return Math.round(y * 10) / 10;
  };

  const calculate = () => {
    const y = computedYards();
    if (!y) {
      toast.error(t("err_measure"));
      return;
    }
    if (isCom && serviceId === "repair") {
      setResult({ yards: y, hourly: true });
      return;
    }
    const addLow =
      (showRemoveOld && removeOld ? 4.5 : 0) +
      (showManyCuts && manyCuts ? 3 : 0) +
      (showSubfloor && subfloor ? 9 : 0);
    const addHigh =
      (showRemoveOld && removeOld ? (isCom ? 11.25 : 9) : 0) +
      (showManyCuts && manyCuts ? 7 : 0) +
      (showSubfloor && subfloor ? 22.5 : 0);
    const baseLow =
      !isCom && showSeam && seamRepair
        ? 11.25
        : isCom
          ? (COMMERCIAL[serviceId]?.[0] ?? service.low)
          : service.low;
    const baseHigh =
      !isCom && showSeam && seamRepair
        ? 18
        : isCom
          ? (COMMERCIAL[serviceId]?.[1] ?? service.high)
          : service.high;
    const stepCount = showStairs ? Math.max(0, parseInt(stairs) || 0) : 0;
    const stepLow = isCom ? 20 : 15;
    const stepHigh = isCom ? 40 : 30;
    setResult({
      yards: y,
      min: Math.round(y * (baseLow + addLow)) + stepCount * stepLow,
      max: Math.round(y * (baseHigh + addHigh)) + stepCount * stepHigh,
    });
  };

  const sendQuote = async () => {
    if (!result) return;
    if (!name.trim() || !phone.trim()) {
      toast.error(t("err_contact"));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          service_type: service.title,
          property_type: propertyType,
          square_yards: result.yards,
          price_min: result.min,
          price_max: result.max,
        }),
      });
      if (!res.ok) throw new Error("failed");
      toast.success(t("quote_sent"));
      setName("");
      setPhone("");
    } catch {
      toast.error(t("quote_fail"));
    } finally {
      setSending(false);
    }
  };

  const bookWith = (extra) =>
    onBook({
      serviceId,
      serviceLabel: service.title,
      yards: result.yards,
      min: result.hourly ? 0 : result.min,
      max: result.hourly ? 0 : result.max,
      propertyType,
      ...extra,
    });

  return (
    <section
      id="quote"
      className="py-16 sm:py-24 lg:py-32 bg-[#0E1A2B] grain relative overflow-hidden"
      data-testid="quote-section"
    >
      <div className="absolute -top-24 left-1/3 h-96 w-96 rounded-full bg-[#F26522]/10 blur-3xl pointer-events-none" />
      <div className="relative px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F26522] font-semibold mb-4">
            {t("quote_eyebrow")}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            {t("quote_h")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-300">
            {t("quote_sub")}
          </p>
          <div className="mt-8 space-y-3 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">
            <p>{t("note1")}</p>
            <p>{t("note2")}</p>
            <p>{t("note3")}</p>
            <p>{t("note4")}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <div className="tracing-beam rounded-3xl">
            <div className="rounded-3xl bg-[#132338] border border-[#203859] p-6 sm:p-8 backdrop-blur-xl">
              <div className="mb-6">
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("prop_type")}
                </label>
                <div className="flex rounded-full bg-[#0E1A2B] p-1 w-max">
                  {[
                    { id: "residential", label: t("prop_res") },
                    { id: "commercial", label: t("prop_com") },
                  ].map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setPropertyType(p.id);
                        setResult(null);
                        setSubfloor(false);
                        setSeamRepair(false);
                      }}
                      className={`rounded-full px-5 py-2 font-display font-bold text-sm transition-[background-color,color] duration-200 ${
                        propertyType === p.id
                          ? "bg-[#F26522] text-white"
                          : "text-slate-400 hover:text-white"
                      }`}
                      data-testid={`quote-prop-${p.id}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {isCom && (
                  <p
                    className="mt-3 text-sm text-slate-400"
                    data-testid="quote-commercial-note"
                  >
                    {t("com_note")}
                  </p>
                )}
              </div>

              <div className="flex rounded-full bg-[#0E1A2B] p-1 mb-6 w-max">
                {[
                  { id: "room", label: t("mode_room") },
                  { id: "yards", label: t("mode_yards") },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      setMode(m.id);
                      setResult(null);
                    }}
                    className={`rounded-full px-5 py-2 font-display font-bold text-sm transition-[background-color,color] duration-200 ${
                      mode === m.id
                        ? "bg-[#F26522] text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                    data-testid={`quote-mode-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {mode === "room" ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                      {t("length_l")}
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                      placeholder="e.g. 15"
                      className="field-input field-input-dark"
                      data-testid="quote-length-input"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                      {t("width_l")}
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      placeholder="e.g. 12"
                      className="field-input field-input-dark"
                      data-testid="quote-width-input"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                    {t("yards_l")}
                  </label>
                  <input
                    type="number"
                      inputMode="decimal"
                    min="0"
                    value={yards}
                    onChange={(e) => setYards(e.target.value)}
                    placeholder="e.g. 40"
                    className="field-input field-input-dark"
                    data-testid="quote-yards-input"
                  />
                </div>
              )}

              <div className="mt-5">
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                  {t("flooring_l")}
                </label>
                <select
                  value={serviceId}
                  onChange={(e) => {
                    setServiceId(e.target.value);
                    setResult(null);
                    setRemoveOld(false);
                    setManyCuts(false);
                    setSeamRepair(false);
                    setSubfloor(false);
                    setStairs("");
                  }}
                  className="field-input field-input-dark"
                  data-testid="quote-service-select"
                >
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {lang === "es" ? s.titleEs : s.title} — {s.price}
                    </option>
                  ))}
                </select>
              </div>

              {showRemoveOld && (
                <label
                  className="mt-4 flex items-center gap-3 rounded-xl border border-[#203859] bg-[#0E1A2B] px-4 py-3 cursor-pointer hover:border-[#F26522]/50 transition-colors duration-200"
                  data-testid="quote-addon-remove-old"
                >
                  <input
                    type="checkbox"
                    checked={removeOld}
                    onChange={(e) => {
                      setRemoveOld(e.target.checked);
                      setResult(null);
                    }}
                    className="h-4 w-4 accent-[#F26522]"
                    data-testid="quote-remove-old-checkbox"
                  />
                  <span className="text-sm text-slate-300">
                    {t("addon_remove")}{" "}
                    <span className="text-[#F26522] font-semibold">
                      {t(isCom ? "addon_remove_price_com" : "addon_remove_price")}
                    </span>
                  </span>
                </label>
              )}

              {showManyCuts && (
                <label
                  className="mt-4 flex items-center gap-3 rounded-xl border border-[#203859] bg-[#0E1A2B] px-4 py-3 cursor-pointer hover:border-[#F26522]/50 transition-colors duration-200"
                  data-testid="quote-addon-many-cuts"
                >
                  <input
                    type="checkbox"
                    checked={manyCuts}
                    onChange={(e) => {
                      setManyCuts(e.target.checked);
                      setResult(null);
                    }}
                    className="h-4 w-4 accent-[#F26522]"
                    data-testid="quote-many-cuts-checkbox"
                  />
                  <span className="text-sm text-slate-300">
                    {t("addon_cuts")}{" "}
                    <span className="text-[#F26522] font-semibold">{t("addon_cuts_price")}</span>
                  </span>
                </label>
              )}

              {showSeam && (
                <label
                  className="mt-4 flex items-center gap-3 rounded-xl border border-[#203859] bg-[#0E1A2B] px-4 py-3 cursor-pointer hover:border-[#F26522]/50 transition-colors duration-200"
                  data-testid="quote-addon-seam-repair"
                >
                  <input
                    type="checkbox"
                    checked={seamRepair}
                    onChange={(e) => {
                      setSeamRepair(e.target.checked);
                      setResult(null);
                    }}
                    className="h-4 w-4 accent-[#F26522]"
                    data-testid="quote-seam-repair-checkbox"
                  />
                  <span className="text-sm text-slate-300">
                    {t("addon_seam")}{" "}
                    <span className="text-[#F26522] font-semibold">{t("addon_seam_price")}</span>
                  </span>
                </label>
              )}

              {showSubfloor && (
                <label
                  className="mt-4 flex items-center gap-3 rounded-xl border border-[#203859] bg-[#0E1A2B] px-4 py-3 cursor-pointer hover:border-[#F26522]/50 transition-colors duration-200"
                  data-testid="quote-addon-subfloor"
                >
                  <input
                    type="checkbox"
                    checked={subfloor}
                    onChange={(e) => {
                      setSubfloor(e.target.checked);
                      setResult(null);
                    }}
                    className="h-4 w-4 accent-[#F26522]"
                    data-testid="quote-subfloor-checkbox"
                  />
                  <span className="text-sm text-slate-300">
                    {t("addon_subfloor")}{" "}
                    <span className="text-[#F26522] font-semibold">{t("addon_subfloor_price")}</span>
                  </span>
                </label>
              )}

              {showStairs && (
                <div className="mt-4">
                  <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                    {t("stairs_l")}{" "}
                    <span className="text-[#F26522] normal-case">
                      {t(isCom ? "stairs_hint_com" : "stairs_hint")}
                    </span>
                  </label>
                  <input
                    type="number"
                      inputMode="decimal"
                    min="0"
                    value={stairs}
                    onChange={(e) => {
                      setStairs(e.target.value);
                      setResult(null);
                    }}
                    placeholder="e.g. 12"
                    className="field-input field-input-dark"
                    data-testid="quote-stairs-input"
                  />
                </div>
              )}

              <button
                onClick={calculate}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-7 py-4 text-base transition-[background-color,transform] duration-200 hover:scale-[1.02] active:scale-95"
                data-testid="calculate-btn"
              >
                <Ruler className="h-5 w-5" />
                {t("calc_btn")}
              </button>

              <AnimatePresence>
                {result && (
                  <motion.div
                    initial={{ opacity: 0, y: 16, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -8, height: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                    data-testid="quote-result"
                  >
                    <div className="mt-6 rounded-2xl bg-[#0E1A2B] border border-[#F26522]/40 p-6">
                      <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                            {t("est_yards")}
                          </p>
                          <p
                            className="font-display font-extrabold text-2xl text-white"
                            data-testid="quote-yards-result"
                          >
                            {result.yards} sq yd
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                            {t("est_range")}
                          </p>
                          <p
                            className="font-display font-extrabold text-3xl text-[#F26522]"
                            data-testid="quote-price-result"
                          >
                            {result.hourly
                              ? t("com_hourly_rate")
                              : `${money(result.min)} – ${money(result.max)}`}
                          </p>
                        </div>
                      </div>

                      {result.hourly ? (
                        <>
                          <p className="mt-4 text-sm text-slate-400">
                            {t("com_hourly_note")}
                          </p>
                          <button
                            onClick={() => bookWith({ hourly: true })}
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0E1A2B] hover:bg-[#F26522] hover:text-white font-display font-bold px-6 py-3.5 text-sm transition-[background-color,color,transform] duration-200 hover:scale-[1.02] active:scale-95"
                            data-testid="quote-book-btn"
                          >
                            <CalendarCheck className="h-4 w-4" />
                            {t("book_visit")}
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <input
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder={t("your_name")}
                              className="field-input field-input-dark"
                              data-testid="quote-name-input"
                            />
                            <input
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              placeholder={t("your_phone")}
                              className="field-input field-input-dark"
                              data-testid="quote-phone-input"
                            />
                          </div>

                          <div className="mt-4 flex flex-col sm:flex-row gap-3">
                            <button
                              onClick={sendQuote}
                              disabled={sending}
                              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#F26522] text-[#F26522] hover:bg-[#F26522] hover:text-white font-display font-bold px-6 py-3.5 text-sm transition-[background-color,color,transform] duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                              data-testid="send-quote-btn"
                            >
                              <Send className="h-4 w-4" />
                              {sending ? t("sending") : t("send_quote")}
                            </button>
                            <button
                              onClick={() => bookWith()}
                              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0E1A2B] hover:bg-[#F26522] hover:text-white font-display font-bold px-6 py-3.5 text-sm transition-[background-color,color,transform] duration-200 hover:scale-[1.02] active:scale-95"
                              data-testid="quote-book-btn"
                            >
                              <CalendarCheck className="h-4 w-4" />
                              {t("book_visit")}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
